/**
 * Conversation History Module
 * Handles saving and loading conversations from Azure Table Storage via API
 */

import { getUserEmail } from "./auth";
import { getConfig } from "./config";

/**
 * Get the API base URL from runtime config
 */
function getApiUrl(): string {
  try {
    const config = getConfig();
    return config.api?.baseUrl || "/api";
  } catch {
    // Fallback if config not loaded yet
    return import.meta.env.VITE_API_URL || "/api";
  }
}

/**
 * Get common headers for API calls including user authentication
 */
function getAuthHeaders(): Record<string, string> {
  const userEmail = getUserEmail();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (userEmail) {
    headers["X-User-Email"] = userEmail;
  }
  return headers;
}

export interface Message {
  role: "user" | "bot";
  text: string;
  timestamp?: string;
}

export interface Conversation {
  conversationId: string;
  title: string;
  timestamp: string;
  messageCount: number;
  messages?: Message[];
}

/**
 * Save current conversation to Azure Table Storage
 */
export async function saveConversation(
  messages: Message[],
  title?: string,
): Promise<string> {
  const userEmail = getUserEmail();

  if (!userEmail) {
    throw new Error("User not authenticated");
  }

  const apiUrl = getApiUrl();
  const response = await fetch(`${apiUrl}/conversations`, {
    method: "POST",
    credentials: "include",
    headers: getAuthHeaders(),
    body: JSON.stringify({
      messages,
      title: title || `Conversation ${new Date().toLocaleString()}`,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to save conversation");
  }

  const data = await response.json();
  return data.conversationId;
}

/**
 * List all conversations for the current user
 */
export async function listConversations(): Promise<Conversation[]> {
  const userEmail = getUserEmail();

  if (!userEmail) {
    throw new Error("User not authenticated");
  }

  const apiUrl = getApiUrl();
  const response = await fetch(`${apiUrl}/conversations/list`, {
    credentials: "include",
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to load conversations");
  }

  const data = await response.json();
  return data.conversations || [];
}

/**
 * Load a specific conversation by ID
 */
export async function loadConversation(
  conversationId: string,
): Promise<Conversation> {
  const userEmail = getUserEmail();

  if (!userEmail) {
    throw new Error("User not authenticated");
  }

  const apiUrl = getApiUrl();
  const response = await fetch(
    `${apiUrl}/conversations/get/${conversationId}`,
    {
      credentials: "include",
      headers: getAuthHeaders(),
    },
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to load conversation");
  }

  const data = await response.json();
  return data; // API returns conversation directly, not wrapped
}
